import SafeMJ from "../../utils/SafeMJ";
import compund_interest from "../assets/compound_interest.es.png";

export default function TAE() {
  return (
    <>
    <p className="mb-3">
      Estas navidades, además de los clásicos anuncios de perfumes seductores y lotería que nunca toca, ha habido un auténtico bombardeo de campañas de bancos
      online y brókers promocionando su TAE. "Vente a Trade Republic y obtén un 2,02% TAE sobre tu efectivo". "Con Revolut, gana hasta un 2,27% de TAE pagado a diario"...
      Pero ¿qué es exactamente esto de la TAE?
    </p>
    <p className="mb-3">
      A primera vista, parece otro de esos conceptos financieros pensados para impresionar más que para explicar. Como ya todos sabemos, a menudo los bancos y 
      economistas emplean esta jerga liosa de siglas y anglicismos para intentar dejar a la población "fuera de juego". Sin embargo, la realidad es que detrás de la TAE 
      no hay nada especialmente misterioso. Lo que hay son matemáticas elementales, y además, una preciosa y sorprendente relación con uno de los números por 
      excelencia de la naturaleza. Hoy, de una forma mucho más sencilla de lo que probablemente imaginas, lo vemos en detalle.
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">1. El famoso interés compuesto</h2>
    <p className="mb-3">
      Imagina que dices: "Venga, va, me abro una de esas cuentas remuneradas de las que tanto habla la gente. Trade Republic, por ejemplo, que me han dicho que va bien", y metes 100€ como capital inicial (<SafeMJ after=").">{"\\(C_0\\)"}</SafeMJ>{" "}
      Ese sería tu <b>efectivo</b>, es decir, tu dinero sin invertir, siempre recuperable.
    </p>
    <p className="mb-3">
      ¿Qué pasa entonces? Pues que Trade Republic te va a pagar por tener tu dinero ahí quieto. 
      Al final ellos utilizan por detrás ese dinero, aunque a tus ojos esté parado, y a cambio te dan una pequeña recompensa proporcional a tu efectivo llamada <b>interés</b>. Ojo, 
      que esto no lo hacen todos los bancos. Aunque pueda sonar loco, muchos de los bancos tradicionales no te dan nada (al menos directamente) a cambio. 
      Pero bueno, ese ya es otro tema que seguramente te interese investigar por tu cuenta.
    </p>
    <p className="mb-3">
      Según la política particular de Trade Republic, ellos dan un 2% anual de intereses distribuidos en pagos diarios (aunque no te lo abonan en la cuenta hasta final de mes, pero vamos, que a
      efectos de beneficios es lo mismo). En otras palabras, al cabo del primer día generas el beneficio asociado a la trescientas sesenta y cincoava parte de ese 2%, pues hay 365 días en un año.
      Es decir, recibirías:
      <SafeMJ>{"\\[100\\cdot\\frac{2\\%}{365}\\,\\text{€}=100\\cdot\\frac{0.02}{365}\\,\\text{€},\\]"}</SafeMJ>
      con lo que al final del primer día tendrías en la cuenta:
      <div className="hidden sm:block">
        <SafeMJ>{"\\[C_1=100+100\\cdot\\frac{0.02}{365}\\,\\text{€}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)\\,\\text{€}.\\]"}</SafeMJ>
      </div>
      <div className="block sm:hidden">
        <SafeMJ>{`
          \\[
          \\begin{aligned}
          C_1&=100+100\\cdot\\frac{0.02}{365}\\,\\text{€} \\\\[6pt]
          &=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)\\,\\text{€}.
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
      Vale, guay, algo es algo. ¿Y a los dos días? Pues aquí hay que tener en cuenta un detalle muy importante: los beneficios que has recibido después del primer día ya forman parte
      de tu capital, y por tanto se deben tener en cuenta en el cálculo de intereses para el día siguiente. Es decir, que los próximos intereses se calculan en base a <SafeMJ after=",">{"\\(C_1\\)"}</SafeMJ>{" "}
      y no sobre <SafeMJ after=".">{"\\(C_0\\)"}</SafeMJ> Este hecho de aplicar los intereses proporcionales sobre el capital más reciente (inicial + beneficios acumulados) es lo que se llama <b>interés compuesto</b>,
      del que seguro tanto has oído hablar últimamente.    
    </p>
    <p className="mb-3">
      Respecto a las cantidades exactas, es fácil observar que al cabo del segundo día acabarías con la siguiente cantidad en tu cuenta:
      <SafeMJ scrollable>{"\\[C_1\\cdot\\left(1+\\frac{0.02}{365}\\right)\\,\\text{€}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^2\\,\\text{€},\\]"}</SafeMJ>
      y como ya puedes deducir, acabarías el año con:
      <SafeMJ scrollable>{"\\[C_{365}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365}\\,\\text{€}.\\]"}</SafeMJ>
    </p>
    <p className="mb-3">
      Si eres observador te habrás dado cuenta de que el exponente anterior, el 365, es en realidad bastante suculento. Imagina que no paramos al año, que seguimos dejando la bolita de nieve
      crecer y crecer... Sabemos entonces que nuestro capital al cabo de <SafeMJ>{"\\(t\\)"}</SafeMJ> años vendrá dado por la función:
      <SafeMJ scrollable>{"\\[C_{365t}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365t}\\,\\text{€},\\]"}</SafeMJ>
      la cual, para nuestra suerte, tiene forma exponencial. Y como ya sabes, ¡las exponenciales acaban creciendo salvajemente!
      Mira por ejemplo, si no, la siguiente evolución de <SafeMJ>{"\\(C_{365t}\\)"}</SafeMJ> para 3 capitales iniciales distintos:
      <img
        src={compund_interest}
        alt="Crecimiento exponencial de 3 capitales iniciales gracias al interés compuesto"
        className="w-max sm:w-[65%] mx-auto my-4"
      />
    </p>
    <p className="mb-3">
      Evidentemente el gráfico anterior es un poco forzado, porque nadie en su sano juicio va a esperar 100 años a retirar su dinero. Pero lo importante es que muestra la naturaleza exponencial del interés compuesto y
      que, con un capital inicial suficientemente grande y un poquito de paciencia, la bolita de nieve puede alcanzar un tamaño muy considerable en no tanto tiempo. De hecho, te invito a que estudies la
      expresión que se obtendría si, además de empezar con el capital inicial <SafeMJ after=",">{"\\(C_0\\)"}</SafeMJ> fueras contribuyendo todos los meses con una aportación adicional. Ya verás como ahí sí que se nota enseguida
      el efecto del interés compuesto...
    </p>
    <p className="mb-3">
      Fantástico, pues una vez entendidos todos estos conceptos, que como has visto no son muy complicados, ya tenemos todo listo para atacar la TAE. Su definición, como verás, resulta sencillísima llegados a este punto.
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">2. ¿Qué es la TAE?</h2>
    <p className="mb-3">
      La TAE es la respuesta a una pregunta natural que cualquier buen ahorrador se haría en el escenario anterior: "¿Cuál va a ser mi tasa
      total de beneficio al cabo del año?" O dicho de un modo algo más rebuscado: "¿Qué interés me tendrían que ofrecer en un único pago a final
      de año para replicar el efecto del interés compuesto?". De hecho, con esta última pregunta tiene todo el sentido ahora que las siglas de
      TAE correspondan a <b>Tasa Anual Equivalente</b>.
    </p>
    <p className="mb-3">
      Bien, pues su cálculo es prácticamente directo. En el ejemplo anterior:
      <div className="hidden lg:block">
        <SafeMJ>{`
          \\[
          \\begin{aligned}
          \\text{TAE}=\\frac{\\text{Beneficios}}{\\text{Capital inicial}}
          &=\\frac{C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365}-C_0}{C_0} \\\\[6pt]
          &=\\left(1+\\frac{0.02}{365}\\right)^{365}-1\\approx 0.02020,
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
      <div className="block lg:hidden">
        <SafeMJ scrollable>{`
          \\[
          \\begin{aligned}
          \\text{TAE}
          &=\\frac{\\text{Beneficios}}{\\text{Capital inicial}} \\\\[6pt]
          &=\\frac{C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365}-C_0}{C_0} \\\\[6pt]
          &=\\left(1+\\frac{0.02}{365}\\right)^{365}-1\\approx 0.02020,
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
      que es justo el famoso 2,02% TAE de Trade Republic.
    </p>
    <p className="mb-3">
    Evidentemente esta fórmula se puede generalizar fácilmente, y de hecho se presenta en muchos textos de la siguiente forma:
      <SafeMJ scrollable>{"\\[\\text{TAE}_{r,n}=\\left(1+\\frac{r}{n}\\right)^{n}-1,\\tag{2.1}\\]"}</SafeMJ>
      siendo:
      <ul className="list-disc list-inside my-3">
        <li><SafeMJ>{"\\(r\\)"}</SafeMJ> el llamado <b>Tipo de Interés Nominal</b> (TIN) expresado en tanto por uno. El 0.02 en nuestro ejemplo.</li>
        <li><SafeMJ>{"\\(n\\)"}</SafeMJ> el número de cobros de intereses (uniformemente distribuidos) a lo largo del año. En nuestro caso, 365.</li>
      </ul>
    </p>
    <p className="mb-3">
      Observamos además que la TAE, al ser un ratio, es adimensional, y que como era de esperar no depende de <SafeMJ after=".">{"\\(C_0\\)"}</SafeMJ> Eso sí, tal y como vimos en la sección anterior,
      a mayor capital inicial, mayor será el beneficio en términos absolutos, aunque no lo sea en términos relativos. Es decir, que ese 2,02% de beneficios anuales será el
      mismo para un mileurista que para un millonario, solo que el millonario le sacará más partido, claro.
    </p>
    <p className="mb-3">
      Otra observación que tiene más interés (nunca mejor dicho) del que parece es que la TAE que hemos obtenido, el 2,02%, es mayor al TIN (2%). Y en verdad tiene todo el sentido, porque seguro que nos dará más beneficio calcular los pagos diarios
      sobre un capital un poquito más grande cada día, que hacerlo siempre en base a los 100€ iniciales. El primer caso sería para <SafeMJ after=",">{"\\(n=365\\)"}</SafeMJ> mientras que el segundo corresponde a <SafeMJ after=",">{"\\(n=1\\)"}</SafeMJ> pues
      <div className="hidden sm:block">
        <SafeMJ>{"\\[C_0(1+0.02)=C_0+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{día 1}}+\\cdots+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{día 365}}.\\]"}</SafeMJ>
      </div>
      <div className="block sm:hidden">
        <SafeMJ scrollable>{`
          \\[
          \\begin{aligned}
          C_0(1+0.02)&=C_0+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{día 1}} \\\\[6pt]
          &\\phantom{=}+\\cdots+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{día 365}}.
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
    </p>
    <p className="mb-3">
      De hecho, gracias a lo anterior ahora vemos claramente algo más general: <b>cuanto mayor sea la frecuencia de pagos, mejor saldremos parados</b> (mayor TAE), pues antes se incorporarán los intereses al capital. O dicho de otro modo: 
      <SafeMJ scrollable>{"\\[\\left(1+\\frac{r}{n+1}\\right)^{n+1}>\\left(1+\\frac{r}{n}\\right)^{n},\\quad \\forall n\\in\\mathbb{N}.\\]"}</SafeMJ>
      Más formalmente, podemos deducir la desigualdad anterior desarrollando mediante el binomio de Newton:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        S_n=\\left(1+\\frac{r}{n}\\right)^{n}
        &=\\sum_{k=0}^{n}\\binom{n}{k} \\frac{r^k}{n^k} \\\\[6pt]
        &=1+1+\\sum_{k=2}^{n}\\frac{r^k}{k!}\\cdot\\frac{(n-1)\\cdots(n-k+1)}{n^{k-1}} \\\\[6pt]
        &=2+\\sum_{k=2}^{n}\\frac{r^k}{k!}\\cdot\\left(1-\\frac{1}{n}\\right)\\cdots\\left(1-\\frac{k-1}{n}\\right).
        \\end{aligned}
        \\]
      `}</SafeMJ>
    </p>
    <p className="mb-3">
      Entonces, si aumentamos <SafeMJ>{"\\(n\\)"}</SafeMJ> a <SafeMJ>{"\\(n+1\\)"}</SafeMJ> estaremos haciendo cada factor <SafeMJ>{"\\(\\bigl(1-\\frac{j}{n}\\bigr)\\)"}</SafeMJ> más grande y a la vez añadiendo un nuevo sumando positivo,
      con lo que efectivamente la sucesión <SafeMJ>{"\\(S_n\\)"}</SafeMJ> es estrictamente creciente.
    </p>
    <p className="mb-3">
      Es más, ya te puedes imaginar la siguiente pregunta natural que se nos debería venir a la cabeza ahora. Si la TAE crece a medida que aumentamos la frecuencia de pagos, ¿qué pasa si pido que me den intereses cada hora?
      ¿Y cada minuto? ¿Por qué no cada milésima de segundo? ¿Y si resulta que al final me hago rico?
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">3. El número de Euler</h2>
    <p className="mb-3">
      Por desgracia para ti, el matemático Jacob Bernoulli ya estudió la sucesión <SafeMJ>{"\\(S_n\\)"}</SafeMJ> en el siglo XVII y confirmó que aquello no se iba al infinito, sino que convergía...
    </p>
    <p className="mb-3">
      Para ello, vamos a empezar con el caso particular de <SafeMJ after=",">{"\\(r=1\\)"}</SafeMJ> es decir, con un TIN que nos duplicaría el dinero a final de año si no aplicásemos el interés compuesto.
      Entonces podemos acotar la sucesión de la siguiente forma:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        S_n&=2+\\sum_{k=2}^{n}\\frac{1}{k!}\\cdot\\left(1-\\frac{1}{n}\\right)\\cdots\\left(1-\\frac{k-1}{n}\\right) \\\\[6pt]
        &\\le 2+\\sum_{k=2}^{n}\\frac{1}{k!}\\le 2+\\sum_{k=1}^{n-1}\\frac{1}{2^k}.
        \\end{aligned}
        \\]
      `}</SafeMJ>
    </p>
    <p className="mb-3">
      De hecho, el sumatorio del final es una suma geométrica, para las cuales conocemos una expresión cerrada. En este caso, para una suma geométrica con razón <SafeMJ>{"\\(R=\\frac{1}{2}\\)"}</SafeMJ> y 
      primer término <SafeMJ after=",">{"\\(a_1=\\frac{1}{2}\\)"}</SafeMJ> se tiene:
      <SafeMJ scrollable>{"\\[\\sum_{k=1}^{n-1}\\frac{1}{2^k}=a_1\\cdot\\frac{R^{n-1}-1}{R-1}=1-\\frac{1}{2^{n-1}}<1,\\]"}</SafeMJ>
      lo que implica que <SafeMJ after=".">{"\\(S_n<3\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      Y ya estaría. Como <SafeMJ>{"\\(S_n\\)"}</SafeMJ> es una sucesión creciente y acotada superiormente, entonces necesariamente debe converger. Y de hecho, si echamos la vista atrás,
      debe hacerlo a un número entre 2 y 3. Dicho límite es, precisamente, como se define el famosísimo número de Euler:   
      <SafeMJ>{"\\[e:=\\lim_{n\\rightarrow\\infty}\\left(1+\\frac{1}{n}\\right)^{n}\\tag{3.1}.\\]"}</SafeMJ>
      Fuerte, ¿eh? Hoy en día es indiscutible que el número <SafeMJ>{"\\(e\\)"}</SafeMJ> es una herramienta clave de las matemáticas, con aplicaciones en variable
      compleja, probabilidad, e incluso teoría de números. Que su origen fuera el estudio del interés compuesto, algo tan humano y cotidiano, es bonito cuando menos, ¿no?
    </p>
    <p className="mb-3">
      Antes de terminar, recuerda que aún tenemos un caso pendiente. ¿Qué pasa si <SafeMJ after="?">{"\\(0\\le r<1\\)"}</SafeMJ>{" "}
      Afortunadamente, la respuesta sigue siendo muy agradable a la vista:
      <SafeMJ>{"\\[\\lim_{n\\rightarrow\\infty}\\left(1+\\frac{r}{n}\\right)^{n}=e^r.\\]"}</SafeMJ>
      Aunque leas en muchos blogs o te diga ChatGPT que la identidad anterior se deduce fácilmente a partir de <SafeMJ after=",">{"\\((3.1)\\)"}</SafeMJ> ya te digo yo que no. No al menos
      sin emplear maquinaria de funciones... Para probarlo de manera más elemental, manteniéndonos en el barro de las sucesiones, hay que tener bastante cuidado. Te dejo <a href="https://www.youtube.com/watch?v=FZEKjsFZfk4"><u>aquí</u></a> una
      demostración muy rigurosa por si te interesa. Sea como sea, lo importante es que la igualdad anterior es cierta. Créeme.
    </p>
    <p className="mb-3">
      Con esto podemos poner un broche final al artículo y observar la relación que existe entre la TAE y el número de Euler a partir de la expresión <SafeMJ after=".">{"\\((2.1)\\)"}</SafeMJ>{" "}
      Como ya habrás observado, se tiene que:
      <SafeMJ>{"\\[\\boxed{\\lim_{n\\rightarrow\\infty}\\text{TAE}_{r,n}=e^r-1}\\]"}</SafeMJ>
    </p>
    </>
  )
}
